import {
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useState } from "react";
import {
  Question,
  useAddNoteMutation,
} from "../../../redux/Hire/interviewQuestionSlice";

type Props = {
  index?: number;
  interviewId?: string;
  question: Question;
};

const QuestionView = ({ index, interviewId, question }: Props) => {
  const [addNote, addNoteMutating] = useAddNoteMutation();
  const [showInput, setShowInput] = useState(false);
  const [comment, setComment] = useState(question.interviewNote || "");
  const [inputValue, setInputValue] = useState(question.interviewNote || "");

  const handleAddNote = useCallback(async () => {
    if (
      inputValue !== (question.interviewNote || "") &&
      inputValue.trim() !== "" &&
      interviewId &&
      question._id
    ) {
      await addNote({
        interviewId,
        questionId: question._id,
        interviewNote: inputValue,
      }).unwrap();
      setInputValue(inputValue);
      setComment(inputValue);
    }
    setShowInput(false);
  }, [addNote, inputValue, interviewId, question._id, question.interviewNote]);

  const handleRemoveNote = useCallback(async () => {
    if (interviewId && question._id) {
      try {
        await addNote({
          interviewId,
          questionId: question._id,
          interviewNote: " ",
        }).unwrap();
        setComment("");
        setInputValue("");
      } catch (e) {
        // Optionally handle error
      }
    }
  }, [addNote, interviewId, question._id]);

  const handleShowInput = useCallback(() => setShowInput(true), []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    },
    [],
  );

  return (
    <Grid container direction="column" gap={2}>
      <Grid item>
        <Typography
          color="#000000"
          fontSize={"14px"}
          fontWeight={"500"}
          lineHeight={"20px"}
        >
          {index !== undefined ? index + 1 : ""}. {question.question}
        </Typography>
      </Grid>

      {/* Show transcription if available, else hint */}
      {question.questionTranscription || question.answerTranscription ? (
        <Grid item bgcolor={"#A16AE80D"} p={2} borderRadius={1}>
          {question.questionTranscription && (
            <>
              <Typography
                fontWeight={500}
                fontSize={12}
                lineHeight={"20px"}
                fontStyle={"italic"}
              >
                Question (as asked):
              </Typography>
              <Typography color="#667086" fontSize={"14px"} mb={1}>
                {question.questionTranscription}
              </Typography>
            </>
          )}
          {question.answerTranscription && (
            <>
              <Typography
                fontWeight={500}
                fontSize={12}
                lineHeight={"20px"}
                fontStyle={"italic"}
              >
                Answer (as spoken):
              </Typography>
              <Typography color="#667086" fontSize={"14px"}>
                {question.answerTranscription}
              </Typography>
            </>
          )}
        </Grid>
      ) : (
        question.hint && (
          <Grid item>
            <Typography
              fontWeight={"500"}
              fontSize={"12px"}
              lineHeight={"20px"}
              fontStyle={"italic"}
            >
              Hints for the answers
            </Typography>
            <Typography color="#667086" fontSize={"14px"}>
              {question.hint}
            </Typography>
          </Grid>
        )
      )}

      {comment.trim() && !showInput && (
        <Grid item>
          <Typography variant="body2" color="success.main">
            Interview Note: {comment}
          </Typography>
        </Grid>
      )}
      <Grid item>
        {!showInput ? (
          <>
            <Button size="small" variant="text" onClick={handleShowInput}>
              {comment.trim() ? "Edit Comment" : "Add Comment"}
            </Button>
            {comment.trim() && (
              <Button size="small" variant="text" onClick={handleRemoveNote}>
                Remove Comment
              </Button>
            )}
          </>
        ) : (
          <TextField
            variant="standard"
            InputProps={{
              disableUnderline: true,
              sx: {
                color: "#000",
                fontSize: "14px",
                lineHeight: "16px",
                gap: 1,
                height: "40px",
                padding: "0 6px",
                border: "1px solid #CCD5FF",
                backgroundColor: "#F8F9FF",
                borderRadius: "50px",
              },

              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    type="submit"
                    variant="contained"
                    size="small"
                    onClick={handleAddNote}
                    sx={{
                      height: "32px",
                      minWidth: "32px",
                      padding: "0 8px",
                      borderRadius: "50px",
                      color: "#A16AE8",
                      textTransform: "none",
                      border: "1px solid #A16AE8",
                      backgroundColor: "#F3EAFF",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "#E9D8FD", // lighter purple on hover
                      },
                    }}
                  >
                    {addNoteMutating.isLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      "Add Comment"
                    )}
                  </Button>
                </InputAdornment>
              ),
            }}
            size="small"
            fullWidth
            autoFocus
            value={inputValue}
            onChange={handleInputChange}
            placeholder="Type your note..."
          />
        )}
      </Grid>
    </Grid>
  );
};

export default QuestionView;
